import React, { createContext, ReactNode, useCallback, useContext, useEffect, useLayoutEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { Locale } from '../types/locale';
import { en, UiKey } from '../data/i18n/en';
import { de } from '../data/i18n/de';
import { fr } from '../data/i18n/fr';
import { pl } from '../data/i18n/pl';
import { ar } from '../data/i18n/ar';
import { phraseMap } from '../data/i18n/phrases';
import { patternsFor } from '../data/i18n/patterns';
import { localeFromPath, localeMeta, localizedPath } from '../utils/locale';
import { createPhraseLookup, startDomTranslation } from '../utils/domTranslator';
type LanguageContextValue = {
  locale: Locale;
  t: (key: UiKey) => string;
  // Translates a whole English phrase — for text that is split into words before rendering
  tr: (text: string) => string;
  switchLocale: (next: Locale) => void;
};
const dictionaries: Record<Locale, Record<UiKey, string>> = {
  en,
  de,
  fr,
  pl,
  ar
};
const LanguageContext = createContext<LanguageContextValue>({
  locale: 'en',
  t: key => en[key],
  tr: text => text,
  switchLocale: () => undefined
});
type LanguageProviderProps = {
  locale: Locale;
  children: ReactNode;
};
export function LanguageProvider({
  locale,
  children
}: LanguageProviderProps) {
  const location = useLocation();
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = localeMeta(locale).dir;
  }, [locale]);

  // Keep visitors in their language: plain site links (/membership, /residences…) gain the language prefix
  useEffect(() => {
    if (locale === 'en') return;
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as Element | null)?.closest?.('a');
      if (!anchor) return;
      const raw = anchor.getAttribute('href');
      if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return;
      if (anchor.target && anchor.target !== '_self' || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href);
      if (url.origin !== window.location.origin || localeFromPath(url.pathname) !== 'en') return;
      e.preventDefault();
      window.location.assign(localizedPath(url.pathname, locale) + url.search + url.hash);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [locale]);
  const switchLocale = useCallback((next: Locale) => {
    if (next === locale) return;
    window.location.assign(localizedPath(location.pathname, next) + location.search + location.hash);
  }, [locale, location.pathname, location.search, location.hash]);

  // Every other English phrase on the page, translated in place as it renders.
  // Logos, wordmarks, contact details and legal text opt out with translate="no" / data-i18n-skip.
  const lookup = useMemo(() => locale === 'en' ? null : createPhraseLookup(phraseMap(locale), patternsFor(locale)), [locale]);
  useLayoutEffect(() => {
    if (!lookup) return;
    return startDomTranslation(lookup);
  }, [lookup]);
  const value = useMemo<LanguageContextValue>(() => {
    const dict = dictionaries[locale];
    return {
      locale,
      t: key => dict[key] ?? en[key],
      tr: text => lookup ? lookup(text) ?? text : text,
      switchLocale
    };
  }, [locale, lookup, switchLocale]);
  return <LanguageContext.Provider value={value} data-id="element-1368" data-mp="2yimcwbr279w8">{children}</LanguageContext.Provider>;
}
export function useLanguage() {
  return useContext(LanguageContext);
}